import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import OrderCard from '../entities/order/ui/OrderCard'
import { isInvalidDateRange } from '../shared/lib/time'
import { exportDiagnostics } from '../shared/lib/exportDiagnostics'
import WorkshopFilters from '../features/order-list/WorkshopFilters'
import { filterWorkshopOrders, summarizeWorkshop } from '../features/order-list/workshopDashboard'
import { buildWorkshopIntervals, workshopBarHeight, workshopPeriodDates } from '../features/order-list/workshopIntervals'
import './Ros.css'

export default function Workshop() {
    const navigate = useNavigate()
    const [orders, setOrders] = useState([])
    const [loading, setLoading] = useState(true)
    const [loadError, setLoadError] = useState('')
    const request = useRef(null)
    const [filterStatus, setFilterStatus] = useState([])
    const [filterMechanic, setFilterMechanic] = useState([])
    const [filterMaster, setFilterMaster] = useState([])
    const [filterBrand, setFilterBrand] = useState([])
    const [vibeMin, setVibeMin] = useState('')
    const [vibeMax, setVibeMax] = useState('')
    const [period, setPeriod] = useState('today')
    const [filterFrom, setFilterFrom] = useState('')
    const [filterTo, setFilterTo] = useState('')
    const [search, setSearch] = useState('')
    const [mechMetric, setMechMetric] = useState('take')
    const [exporting, setExporting] = useState(false)
    const { token } = useAuth()

    const loadData = useCallback(() => {
        request.current?.abort()
        if (period === 'custom' && isInvalidDateRange(filterFrom, filterTo)) {
            setLoading(false)
            return
        }
        const controller = new AbortController()
        request.current = controller
        const { from, to } = workshopPeriodDates(period, filterFrom, filterTo)
        // This page's seven-day week includes today; do not let the legacy API expand it to eight days.
        const params = new URLSearchParams({ period: period === 'week' ? 'custom' : period })
        if (from) params.set('from', from)
        if (to) params.set('to', to)
        setLoading(true)
        setLoadError('')
        fetch(`/api/orders?${params}`, { signal: controller.signal, headers: { Authorization: `Bearer ${token}` } })
            .then(async response => {
                if (!response.ok) throw new Error('Не удалось загрузить заявки. Повторите попытку.')
                const data = await response.json()
                if (!Array.isArray(data)) throw new Error('Не удалось загрузить заявки. Повторите попытку.')
                if (!controller.signal.aborted) setOrders(data)
            }).catch(error => {
                if (!controller.signal.aborted) setLoadError(error.message)
            }).finally(() => { if (!controller.signal.aborted) setLoading(false) })
    }, [token, period, filterFrom, filterTo])

    useEffect(() => { loadData(); return () => request.current?.abort() }, [loadData])

    const invalidPeriod = period === 'custom' && isInvalidDateRange(filterFrom, filterTo)
    const filteredOrders = useMemo(() => loading || loadError || invalidPeriod ? [] : filterWorkshopOrders(orders, {
        search, statuses: filterStatus, mechanics: filterMechanic, masters: filterMaster,
        brands: filterBrand, min: vibeMin, max: vibeMax,
    }), [orders, loading, loadError, invalidPeriod, search, filterStatus, filterMechanic, filterMaster, filterBrand, vibeMin, vibeMax])
    // Bounds use the full accessible period, not the search result, so empty intervals stay in place.
    const intervals = useMemo(() => loading || loadError || invalidPeriod ? [] : buildWorkshopIntervals({
        period, from: filterFrom, to: filterTo, orders,
    }), [orders, loading, loadError, invalidPeriod, period, filterFrom, filterTo])
    const workshopData = useMemo(() => summarizeWorkshop(filteredOrders, intervals), [filteredOrders, intervals])
    const matchingStats = workshopData
    const stats = workshopData

    useEffect(() => {
        if (!token) return
        let socket
        let reconnectTimeout
        let isUnmounted = false

        const connect = () => {
            const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws'
            socket = new WebSocket(`${protocol}://${window.location.host}/api/ws?token=${token}`)
            socket.onmessage = (event) => {
                try {
                    const data = JSON.parse(event.data)
                    if (data.type === 'orders_changed') loadData()
                } catch { /* ignore unrelated/malformed socket messages */ }
            }
            socket.onclose = () => {
                if (isUnmounted) return
                reconnectTimeout = setTimeout(connect, 5000)
            }
        }

        connect()

        return () => {
            isUnmounted = true
            clearTimeout(reconnectTimeout)
            if (socket) socket.close()
        }
    }, [token, loadData])

    useEffect(() => {
        if (!workshopData?.dynamics?.length) return
        let chartInstance = null
        let disposed = false

        const loadChart = async () => {
            await new Promise(r => setTimeout(r, 50))
            const canvas = document.getElementById('workshopChart')
            if (!canvas) return
            const { Chart, registerables } = await import('https://cdn.jsdelivr.net/npm/chart.js@4.4.1/+esm')
            if (disposed || !canvas.isConnected) return
            Chart.register(...registerables)
            const existing = Chart.getChart(canvas)
            if (existing) existing.destroy()
            if (chartInstance) chartInstance.destroy()
            const pts = workshopData.dynamics
            chartInstance = new Chart(canvas, {
                type: 'line',
                data: {
                    labels: pts.map(d => d.label),
                    datasets: [
                        {
                            label: 'Ожидание',
                            data: pts.map(d => d.avg_take),
                            borderColor: '#0052FF',
                            pointBorderColor: '#0052FF',
                            pointBackgroundColor: 'white',
                            borderWidth: 2.5,
                            pointRadius: 4,
                            pointHoverRadius: 5,
                            pointBorderWidth: 2,
                            tension: 0,
                            spanGaps: true,
                        },
                        {
                            label: 'В работе',
                            data: pts.map(d => d.avg_work),
                            borderColor: '#10B981',
                            pointBorderColor: '#10B981',
                            pointBackgroundColor: 'white',
                            borderWidth: 2.5,
                            pointRadius: 4,
                            pointHoverRadius: 5,
                            pointBorderWidth: 2,
                            tension: 0,
                            spanGaps: true,
                        }
                    ]
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    layout: { padding: { top: 8, right: 8, bottom: 4, left: 8 } },
                    interaction: { mode: 'index', intersect: false },
                    plugins: { legend: { display: false }, tooltip: { mode: 'index', intersect: false, callbacks: {
                        title: items => pts[items[0]?.dataIndex]?.tooltip || '',
                        label: item => `${item.dataset.label}: ${item.parsed.y} мин`,
                        afterBody: items => {
                            const point = pts[items[0]?.dataIndex]
                            return point ? [point.avg_take == null ? 'Ожидание: нет данных' : '', point.avg_work == null ? 'Диагностика: нет данных' : ''].filter(Boolean) : []
                        },
                    } } },
                    scales: {
                        x: { display: true, grid: { display: false }, border: { display: false }, ticks: {
                            autoSkip: true, maxTicksLimit: 12, minRotation: 0, maxRotation: 45,
                            font: { size: 10 }, color: '#9CA3AF',
                        } },
                        y: { display: false, min: 0 }
                    }
                }
            })
        }
        loadChart().catch(() => {})
        return () => { disposed = true; if (chartInstance) chartInstance.destroy() }
    }, [workshopData])

    const mechanics = [...new Set(orders.map(o => o.mechanic_name).filter(Boolean))]
    const masters = [...new Set(orders.map(o => o.master_name).filter(Boolean))]
    const brands = [...new Set(orders.map(o => o.car_brand).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'ru'))

    const handleExportXlsx = async () => {
        setExporting(true)
        try {
            await exportDiagnostics({ orders: filteredOrders, token, filters: {
                period,
                from: filterFrom,
                to: filterTo,
                status: filterStatus.join(','),
                mechanic: filterMechanic.join(','),
                master: filterMaster.join(','),
                brand: filterBrand.join(','),
                vibeMin,
                vibeMax,
                search,
            } })
        } catch (e) { console.error(e) }
        setExporting(false)
    }

    const mechanicStats = workshopData?.mechanicStats || []
    const activeMechanics = mechanicStats.filter(m => parseInt(m.in_progress) > 0 || parseInt(m.completed_today) > 0 || parseInt(m.expired_today) > 0)
    const dynamicData = workshopData?.dynamics || []
    const expiredOpen = matchingStats.expiredOpen
    const expiredProgress = matchingStats.expiredProgress
    const card = { background: 'white', borderRadius: '18px', padding: '18px', boxShadow: '0 1px 2px rgba(16,24,40,.04),0 4px 14px -6px rgba(16,24,40,.08)' }

    const avgTake = workshopData?.avgTakeMinutes ?? '—'
    const avgWork = workshopData?.avgWorkMinutes ?? '—'
    const chartMinWidth = dynamicData.length > 10 ? `${dynamicData.length * 42}px` : '100%'
    const timingChartHeight = Math.min(280, 160 + Math.floor(dynamicData.length / 12) * 24)
    const intervalTiming = interval => `${interval.tooltip}. Ожидание: ${interval.avg_take == null ? 'нет данных' : `${interval.avg_take} мин`}. Диагностика: ${interval.avg_work == null ? 'нет данных' : `${interval.avg_work} мин`}`

    return (
        <div className="ros-page">
            <WorkshopFilters period={period} setPeriod={setPeriod} from={filterFrom} to={filterTo} setFrom={setFilterFrom} setTo={setFilterTo}
                search={search} setSearch={setSearch} min={vibeMin} max={vibeMax} setMin={setVibeMin} setMax={setVibeMax}
                loading={loading}
                filters={[
                    { key: 'status', label: 'Статус', value: filterStatus, set: setFilterStatus, options: [{ value: 'open', label: 'Новая' }, { value: 'in_progress', label: 'В работе' }, { value: 'closed', label: 'Завершена' }, { value: 'sent', label: 'Отправлена' }, { value: 'expired_open', label: 'Пропущена' }, { value: 'expired_progress', label: 'Брошена' }] },
                    { key: 'mechanic', label: 'Механик', value: filterMechanic, set: setFilterMechanic, options: mechanics.map(value => ({ value, label: value })) },
                    { key: 'master', label: 'Мастер-консультант', value: filterMaster, set: setFilterMaster, options: masters.map(value => ({ value, label: value })) },
                    { key: 'brand', label: 'Марка', value: filterBrand, set: setFilterBrand, options: brands.map(value => ({ value, label: value })) },
                ]}
                onReset={() => { setSearch(''); setFilterStatus([]); setFilterMechanic([]); setFilterMaster([]); setFilterBrand([]); setVibeMin(''); setVibeMax('') }}
            />
            {loading && <p role="status" style={{ margin: 0, fontSize: '13px', color: '#6B7280' }}>Загрузка показателей…</p>}
            {loadError && <div role="alert" style={card}>{loadError} <button type="button" onClick={loadData}>Повторить</button></div>}

            <>
                <style>{`
                            .ws-metric-tiles-grid {
                                display: grid;
                                gap: 10px;
                                grid-template-columns: repeat(2, 1fr);
                                grid-template-areas: "all prog" "done exp";
                            }
                            @media (min-width: 768px) {
                                .ws-metric-tiles-grid {
                                    grid-template-columns: repeat(4, 1fr);
                                    grid-template-areas: "all prog done exp";
                                }
                            }
                        `}</style>
                <div className="ws-metric-tiles-grid">
                    {[
                        { key: 'all', label: 'Все', bg: '#0052FF', icon: 'ti-list', area: 'all', value: matchingStats.total },
                        { key: 'in_progress', label: 'В работе', bg: '#F59E0B', icon: 'ti-tool', area: 'prog', value: matchingStats.inProgress },
                        { key: 'done', label: 'Завершено', bg: '#10B981', icon: 'ti-check', area: 'done', value: matchingStats.sent + matchingStats.closedNotSent },
                        { key: 'expired', label: 'Просрочено', bg: '#DC2626', icon: 'ti-alert-triangle', area: 'exp', value: matchingStats.expired },
                    ].map(m => (
                        <div key={m.key} className="ros-tile" style={{ gridArea: m.area, background: m.bg, cursor: 'default' }}>
                            <div style={{ fontSize: '20px', fontWeight: '700', color: 'white' }}>{m.value}</div>
                            {m.key === 'done' && (
                                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.65)', margin: '2px 0' }}>
                                    {matchingStats.sent} SMS · {matchingStats.closedNotSent} Без SMS
                                </div>
                            )}
                            {m.key === 'expired' && (
                                <div style={{ fontSize: '9px', color: 'rgba(255,255,255,0.65)', margin: '2px 0' }}>
                                    {expiredOpen} пропущено · {expiredProgress} брошено
                                </div>
                            )}
                            <div style={{ fontSize: '11px', fontWeight: '600', color: 'white', opacity: 0.9 }}>{m.label}</div>
                        </div>
                    ))}
                </div>
            </>

            {dynamicData.length > 0 && (
                <div style={card} className="ros-half-left">
                    <p style={{ margin: '0 0 4px', fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>Динамика времён</p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', marginTop: '14px', marginBottom: '14px' }}>
                        <div className="ros-hero" data-tone="blue">
                            <div style={{ fontSize: '10px', color: '#1E40AF', marginBottom: '2px' }}>Ср. ожидание</div>
                            <div style={{ fontSize: '22px', fontWeight: '800', color: '#0052FF' }}>{avgTake} <span style={{ fontSize: '11px', color: '#93C5FD', fontWeight: '500' }}>мин</span></div>
                        </div>
                        <div className="ros-hero" data-tone="green">
                            <div style={{ fontSize: '10px', color: '#065F46', marginBottom: '2px' }}>Ср. диагностика</div>
                            <div style={{ fontSize: '22px', fontWeight: '800', color: '#10B981' }}>{avgWork} <span style={{ fontSize: '11px', color: '#6EE7B7', fontWeight: '500' }}>мин</span></div>
                        </div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '10px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <div style={{ width: '16px', height: '3px', background: '#0052FF', borderRadius: '2px' }} />
                            <span style={{ fontSize: '11px', color: '#6B7280' }}>Ожидание</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <div style={{ width: '16px', height: '3px', background: '#10B981', borderRadius: '2px' }} />
                            <span style={{ fontSize: '11px', color: '#6B7280' }}>В работе</span>
                        </div>
                    </div>
                    <div className="ws-timing-chart" style={{ position: 'relative', width: '100%', minWidth: 0, height: `${timingChartHeight}px` }}>
                        <canvas id="workshopChart" style={{ display: 'block', position: 'absolute', inset: 0, width: '100%', maxWidth: '100%', height: '100%' }} role="img" aria-label="График динамики времён ожидания и диагностики. Линии соединяют измеренные точки; отсутствие точки не означает нулевое время.">
                            {dynamicData.map(intervalTiming).join('\n')}
                        </canvas>
                    </div>
                </div>
            )}

            {stats?.dynamics?.length > 0 && (
                <div style={{ ...card, minWidth: 0 }} className="ros-half-right">
                    <p style={{ margin: '0 0 14px', fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>Динамика заявок</p>
                    <div style={{ overflowX: 'auto' }}>
                    <div style={{ display: 'flex', alignItems: 'flex-end', gap: '5px', minWidth: chartMinWidth, height: '120px' }}>
                        {stats.dynamics.map((d, i) => {
                            const maxDynamic = Math.max(...stats.dynamics.map(d => parseInt(d.count)))
                            const h = workshopBarHeight(d.count, maxDynamic)
                            const isMax = d.count > 0 && d.count === maxDynamic
                            return (
                                <div key={i} className="ws-count-interval ros-count-interval" tabIndex={0} title={`${d.tooltip}. Количество: ${d.count}`} aria-label={`${d.tooltip}. Количество: ${d.count}`} style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontSize: '10px', fontWeight: '600', color: isMax ? 'white' : '#9CA3AF', background: isMax ? '#0052FF' : 'transparent', borderRadius: '4px', padding: isMax ? '1px 5px' : '0' }}>{d.count}</span>
                                    <div data-count={d.count} className="ros-bar" data-state={isMax ? 'max' : d.count > 0 ? 'some' : 'zero'} style={{ width: '100%', maxWidth: '40px', height: `${h}px` }} />
                                    <span style={{ fontSize: '10px', color: '#9CA3AF', textAlign: 'center', minHeight: '28px', overflowWrap: 'anywhere' }}>{d.label}</span>
                                </div>
                            )
                        })}
                    </div>
                    </div>
                </div>
            )}

            {activeMechanics.length > 0 && (() => {
                const metricField = mechMetric === 'take' ? 'avg_take_minutes' : 'avg_work_minutes'
                const metricColor = mechMetric === 'take' ? '#374151' : '#1E40AF'
                const sorted = [...activeMechanics].filter(m => m[metricField] != null).sort((a, b) => Number(a[metricField]) - Number(b[metricField]))
                const fastest = sorted[0]
                const slowest = sorted[sorted.length - 1]
                return (
                    <div style={card}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                            <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>Механики</p>
                            <div className="ros-switch">
                                <button onClick={() => setMechMetric('take')} aria-pressed={mechMetric === 'take'}>Взятие</button>
                                <button onClick={() => setMechMetric('work')} aria-pressed={mechMetric === 'work'}>Диагностика</button>
                            </div>
                        </div>
                        {fastest && slowest && fastest.name !== slowest.name && (
                            <div style={{ display: 'flex', gap: '8px', marginBottom: '12px' }}>
                                <div className="ros-hero" data-tone="fast">
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#0052FF', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Быстрее всех</div>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>{fastest.name}</div>
                                    <div style={{ fontSize: '22px', fontWeight: '800', color: '#1A1A1A' }}>{fastest[metricField]} <span style={{ fontSize: '11px', fontWeight: '500', color: '#9CA3AF' }}>мин</span></div>
                                    <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '4px' }}>{fastest.completed_today || 0} завершено · {fastest.expired_today || 0} просрочено</div>
                                </div>
                                <div className="ros-hero" data-tone="slow">
                                    <div style={{ fontSize: '10px', fontWeight: '600', color: '#DC2626', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>Медленнее всех</div>
                                    <div style={{ fontSize: '13px', fontWeight: '600', color: '#1A1A1A', marginBottom: '4px' }}>{slowest.name}</div>
                                    <div style={{ fontSize: '22px', fontWeight: '800', color: '#1A1A1A' }}>{slowest[metricField]} <span style={{ fontSize: '11px', fontWeight: '500', color: '#9CA3AF' }}>мин</span></div>
                                    <div style={{ fontSize: '10px', color: '#9CA3AF', marginTop: '4px' }}>{slowest.completed_today || 0} завершено · {slowest.expired_today || 0} просрочено</div>
                                </div>
                            </div>
                        )}
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 36px 36px 36px 56px', gap: '4px', alignItems: 'center', padding: '6px 0' }}>
                            <span></span>
                            <span style={{ fontSize: '13px', color: '#F59E0B', textAlign: 'center' }}><i className="ti ti-tool" /></span>
                            <span style={{ fontSize: '13px', color: '#10B981', textAlign: 'center' }}><i className="ti ti-check" /></span>
                            <span style={{ fontSize: '13px', color: '#DC2626', textAlign: 'center' }}><i className="ti ti-alert-triangle" /></span>
                            <span style={{ fontSize: '13px', color: '#9CA3AF', textAlign: 'right' }}><i className="ti ti-clock" /></span>
                        </div>
                        {activeMechanics.map((m, i) => (
                            <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 36px 36px 36px 56px', gap: '4px', alignItems: 'center', padding: '10px 0', borderTop: '1px solid #F1F3F7' }}>
                                <span style={{ fontSize: '13px', color: '#1A1A1A', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{m.name}</span>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: parseInt(m.in_progress) > 0 ? '#F59E0B' : '#6B7280', textAlign: 'center' }}>{m.in_progress || 0}</span>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: '#10B981', textAlign: 'center' }}>{m.completed_today || 0}</span>
                                <span style={{ fontSize: '13px', fontWeight: '700', color: parseInt(m.expired_today) > 0 ? '#DC2626' : '#6B7280', textAlign: 'center' }}>{m.expired_today || 0}</span>
                                <span style={{ fontSize: '12px', fontWeight: '600', color: metricColor, textAlign: 'right' }}>{m[metricField] ?? '—'} мин</span>
                            </div>
                        ))}
                        <p className="ros-icon-legend">
                            <span><i className="ti ti-tool" style={{ color: '#F59E0B' }} aria-hidden="true" />в работе</span>
                            <span><i className="ti ti-check" style={{ color: '#10B981' }} aria-hidden="true" />завершено</span>
                            <span><i className="ti ti-alert-triangle" style={{ color: '#DC2626' }} aria-hidden="true" />просрочено</span>
                            <span><i className="ti ti-clock" style={{ color: '#9CA3AF' }} aria-hidden="true" />{mechMetric === 'take' ? 'среднее взятие' : 'средняя диагностика'}</span>
                        </p>
                    </div>
                )
            })()}

            <div style={card}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <p style={{ margin: 0, fontSize: '14px', fontWeight: '600', color: '#1A1A1A' }}>
                        Диагностики <span style={{ fontSize: '12px', color: '#9CA3AF', fontWeight: '600', marginLeft: '4px' }}>{filteredOrders.length}</span>
                    </p>
                    {filteredOrders.length > 0 && (
                        <button onClick={handleExportXlsx} disabled={exporting} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '6px 11px', borderRadius: '10px', border: 'none', background: '#ECFDF5', color: '#059669', fontSize: '12px', fontWeight: '600', cursor: exporting ? 'not-allowed' : 'pointer', opacity: exporting ? 0.7 : 1 }}>
                            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#059669" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
                            {exporting ? 'Загрузка...' : 'xlsx'}
                        </button>
                    )}
                </div>
                {filteredOrders.length === 0
                    ? <p style={{ color: '#9CA3AF', fontSize: '13px', textAlign: 'center', padding: '20px 0' }}>Нет диагностик</p>
                    : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        {filteredOrders.map(order => (
                            <OrderCard
                                key={order.id}
                                order={order}
                                onClick={(o) => navigate(`/orders/${o.id}`)}
                                showMechanic={true}
                                showSendingReminder={true}
                            />
                        ))}
                    </div>
                }
            </div>

        </div>

    )
}
